import { useMutation } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { DELETE_POSTER, UPDATE_POSTER, UPLOAD_POSTER_ASSET } from '../../../components/apolloQueries';
import { PosterType, type Poster } from '../../../../../../__gqltypes__';
import DeleteVanityModal from './DeleteVanityModal';
import DragUpload from './DragUpload';
import AssetDisplay from './AssetDisplay';
import OwnershipStatsCard from '../../../../devtools/screens/VanityItemTools/OwnershipStatsCard';

type Props = {
  item: Poster;
  refetch: () => void;
};

export default function PosterView({ item, refetch }: Props) {
  const [itemData, setItemData] = useState(item);

  useEffect(() => {
    setItemData(item);
  }, [item]);
  const [updatePoster, { loading }] = useMutation(UPDATE_POSTER, {
    onCompleted: () => refetch(),
  });
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [deletePoster] = useMutation(DELETE_POSTER, {
    onCompleted: () => refetch(),
  });

  const [uploadAsset, { loading: assetLoading }] = useMutation(UPLOAD_POSTER_ASSET);

  const handleUpload = (file: Blob) => {
    uploadAsset({
      variables: {
        input: {
          file,
          id: itemData.id,
        },
      },
    }).then(({ data }) => {
      setItemData(data.uploadPosterAssets);
    });
  };

  const handleUpdate = () => {
    updatePoster({
      variables: {
        input: {
          id: itemData?.id,
          name: itemData?.name,
          currencyCost: itemData?.currencyCost,
          currencyType: itemData?.currencyType,
          itemId: itemData?.itemId,
          asset: itemData?.asset,
        },
      },
    });
  };

  const handleDelete = () => {
    deletePoster({ variables: { posterId: itemData?.id } });
    history.push('/sp3/liveops/items/posters');
    setShowDeleteModal(false);
  };

  const iterate = (root: Record<string, string> | string, acc: string[]) => {
    if (_.isString(root)) {
      if (root.startsWith('https')) {
        acc.push(root);
      }
      return;
    }
    if (_.isArray(root)) {
      root.forEach((r) => iterate(r, acc));
      return;
    }

    if (!_.isObject(root)) {
      return;
    }

    const val = Object.values(root);
    val.forEach((v) => iterate(v, acc));
  };

  const handleValidateJsonItem = (f: Blob): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (evt) => {
        let jsonObj = {};
        const validationErrors = [];
        try {
          // @ts-ignore
          jsonObj = JSON.parse(evt.target.result);
        } catch (e) {
          console.error('not valid JSON file', e);
          validationErrors.push('not valid JSON file');
        }

        const acc: string[] = [];
        iterate(jsonObj, acc);
        for (const url of acc) {
          try {
            const response = await fetch(url);
            if (response.status !== 200) {
              validationErrors.push(`${url} not found`);
            }
          } catch (e) {
            validationErrors.push(`${url} not found`);
          }
        }
        resolve(validationErrors);
      };

      reader.readAsText(f);
    });
  };

  return (
    <>
      <Card className="mt-3">
        <LoadingLogo show={loading} />
        <DeleteVanityModal
          type="Poster"
          name={itemData?.name}
          show={showDeleteModal && !loading}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Card.Header className="text-center d-flex justify-content-between">
          <h4>{itemData?.name}</h4>
          <div>
            <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </div>
        </Card.Header>
        <Card.Body style={{ height: '100%' }}>
          <Form>
            <Form.Group>
              <Form.Label>Id</Form.Label>
              <Form.Control placeholder={itemData?.id} disabled />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder=""
                value={itemData?.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Control as="select" value={itemData?.type} disabled>
                {Object.keys(PosterType).map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {itemData?.type !== PosterType.POSTER && (
              <Form.Group>
                <Form.Label>Item ID</Form.Label>
                <Form.Control
                  value={itemData?.itemId ?? undefined}
                  onChange={(e) =>
                    setItemData({
                      ...itemData,
                      itemId: e.target.value,
                    })
                  }
                />
                <Form.Label>Currency type</Form.Label>
                <Form.Control
                  as="select"
                  value={itemData?.currencyType ?? ''}
                  onChange={(e) =>
                    setItemData({
                      ...itemData,
                      currencyType: e.target.value,
                    })
                  }
                >
                  {['CurrencyDiamond', 'CurrencyCoin'].map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </Form.Control>
                <Form.Label>Cost</Form.Label>
                <Form.Control
                  value={itemData?.currencyCost ?? ''}
                  onChange={(e) =>
                    setItemData({
                      ...itemData,
                      currencyCost: +e.target.value,
                    })
                  }
                />
              </Form.Group>
            )}
            <hr />
            <LoadingLogo show={assetLoading} />

            <DragUpload onUpload={handleUpload} onValidate={handleValidateJsonItem} />
            <Form.Group>
              <Form.Label>Asset</Form.Label>
              <Form.Control value={itemData?.asset ?? ''} disabled />
            </Form.Group>
            {!!itemData?.asset?.length && <AssetDisplay assetUrl={itemData?.asset} />}
          </Form>
        </Card.Body>
      </Card>
      {itemData?.id && <OwnershipStatsCard category="POSTER" itemId={itemData.id} />}
    </>
  );
}
